import { useState, useEffect, useRef, useMemo } from "react";
import { ChevronLeft, ChevronRight, Plus, Search, Star } from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toolsList, ToolId, getToolColorFromCategory } from "@packages/utils";
import { motion, AnimatePresence } from "framer-motion";
import { trackEvent } from "@packages/hooks/useAnalytics";
import { Button, Text } from "@core/index";
import FavoriteButton from "@features/tools/components/favorite-button";
import { useAppDispatch, useAppSelector } from "@packages/store/hooks";
import { RootState } from "@packages/store/store";

import { toggleFavoriteTool } from "@packages/store/userSlice";

const NEW_TOOL_IDS = [
  "ai-supported-tasks",
  "blooms-taxonomy-activity",
  "ai-assignment-designer",
  "ai-assessment-scale",
  "transferActivities",
  "ai-assessment-scale",
  "performance-task",
  "essentialQuestions",
  "ai-resistant-homework-idea",
  "heygenVideoGenerator",
];

// Helper function to get tab colors based on category
const getTabColors = (tabName: string) => {
  switch (tabName) {
    case "favorites":
      return {
        active:
          "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 border border-purple-200 dark:border-purple-700 hover:border-purple-300 dark:hover:border-purple-600",
        inactive:
          "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
      };
    case "all":
      return {
        active:
          "bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600",
        inactive:
          "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
      };
    case "content-development":
      return {
        active:
          "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border border-green-200 dark:border-green-700 hover:border-green-300 dark:hover:border-green-600",
        inactive:
          "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
      };
    case "activity-planning":
      return {
        active:
          "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300 border border-orange-200 dark:border-orange-700 hover:border-orange-300 dark:hover:border-orange-600",
        inactive:
          "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
      };
    case "assessment-evaluation":
      return {
        active:
          "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 border border-blue-200 dark:border-blue-700 hover:border-blue-300 dark:hover:border-blue-600",
        inactive:
          "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
      };
    default:
      return {
        active:
          "bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 hover:border-indigo-300 dark:hover:border-indigo-600",
        inactive:
          "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
      };
  }
};

const Tools = () => {
  const { t } = useTranslation();
  const { trackEvent: track } = trackEvent();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const category = searchParams.get("category");
  const [activeTab, setActiveTab] = useState(category || "all");
  const [searchTerm, setSearchTerm] = useState("");

  const [filteredTools, setFilteredTools] = useState(toolsList[0]?.tools || []);

  const dispatch = useAppDispatch();
  const favoriteTools = useAppSelector(
    (state: RootState) => state.user.teacher?.preferences?.favoriteTools,
  );

  useEffect(() => {
    if (category) {
      setActiveTab(category);
    } else {
      setActiveTab("all");
    }
  }, [category]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);

    if (tab === "all") {
      // Remove category parameter for "all" tab
      searchParams.delete("category");
    } else {
      // Set category parameter for other tabs
      searchParams.set("category", tab);
    }

    setSearchParams(searchParams);
  };

  useEffect(() => {
    // First, get the base filtered tools according to the active tab
    let baseFilteredTools = [];
    if (activeTab === "favorites") {
      baseFilteredTools = toolsList.flatMap((category) =>
        category.tools.filter((tool) =>
          favoriteTools?.includes(tool.id as ToolId),
        ),
      );
    } else if (activeTab === "all") {
      baseFilteredTools = toolsList.flatMap((category) => category.tools);
    } else {
      const category = toolsList.find((cat) => cat.category === activeTab);
      baseFilteredTools = category ? category.tools : [];
    }

    // Then apply search filter on the already filtered tools
    if (searchTerm !== "") {
      baseFilteredTools = baseFilteredTools.filter(
        (tool) =>
          t(tool.title).toLowerCase().includes(searchTerm.toLowerCase()) ||
          t(tool.description).toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredTools(baseFilteredTools);
  }, [searchTerm, activeTab, favoriteTools, t]);

  const handleClickDescription = (toolPath: string) => {
    track(
      {
        category: "ContentDescription",
        action: "tools.description",
        label: "Tools description click",
        nonInteraction: false,
      },
      {
        location: "/tools/description",
        element: "description-button",
      },
    );
    navigate(toolPath);
  };

  const handleClickGeneration = (createPath: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent parent click handler
    track(
      {
        category: "ContentGeneration",
        action: "tools.generate",
        label: "Tools generation click",
        nonInteraction: false,
      },
      {
        location: "/tools/generate",
        element: "generate-button",
      },
    );
    navigate(createPath);
  };

  const toggleFavorite = async (toolId: ToolId) => {
    try {
      dispatch(toggleFavoriteTool(toolId));
    } catch (error) {
      console.error("Favori araçları güncellerken hata oluştu:", error);
    }
  };

  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const leftButtonRef = useRef<HTMLButtonElement>(null);
  const rightButtonRef = useRef<HTMLButtonElement>(null);

  const scrollAmount = 200;

  const scrollLeft = () => {
    if (tabsContainerRef.current) {
      tabsContainerRef.current.scrollLeft -= scrollAmount;
    }
  };

  const scrollRight = () => {
    if (tabsContainerRef.current) {
      tabsContainerRef.current.scrollLeft += scrollAmount;
    }
  };

  const handleScroll = () => {
    if (!tabsContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = tabsContainerRef.current;

    if (leftButtonRef.current) {
      leftButtonRef.current.style.opacity = scrollLeft > 20 ? "1" : "0";
    }

    if (rightButtonRef.current) {
      rightButtonRef.current.style.opacity =
        scrollLeft < scrollWidth - clientWidth - 20 ? "1" : "0";
    }
  };

  useEffect(() => {
    const checkForOverflow = () => {
      if (!tabsContainerRef.current) return;

      const { scrollWidth, clientWidth } = tabsContainerRef.current;
      const hasOverflow = scrollWidth > clientWidth;

      if (rightButtonRef.current) {
        rightButtonRef.current.style.opacity = hasOverflow ? "1" : "0";
      }

      if (leftButtonRef.current) {
        leftButtonRef.current.style.opacity = "0";
      }
    };

    checkForOverflow();

    const resizeObserver = new ResizeObserver(checkForOverflow);

    if (tabsContainerRef.current) {
      resizeObserver.observe(tabsContainerRef.current);
    }

    return () => {
      if (tabsContainerRef.current) {
        resizeObserver.unobserve(tabsContainerRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []);

  // Calculate the maximum description length for consistent card heights
  const maxDescriptionHeight = useMemo(() => {
    const currentTools =
      activeTab === "all"
        ? toolsList.flatMap((category) =>
            category.tools.filter((tool) => tool.id !== "essay-grader"),
          )
        : filteredTools.filter((tool) => tool.id !== "essay-grader");

    if (!currentTools.length) return 0;

    // Create a temporary element to measure text height
    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.visibility = "hidden";
    tempDiv.style.width = "100%";
    tempDiv.style.fontSize = "14px";
    tempDiv.style.lineHeight = "1.5";
    tempDiv.style.padding = "0";
    tempDiv.style.margin = "0";
    tempDiv.className = "text-gray-600 dark:text-gray-400";
    document.body.appendChild(tempDiv);

    let maxHeight = 0;
    currentTools.forEach((tool) => {
      if (
        searchTerm === "" ||
        t(tool.title).toLowerCase().includes(searchTerm.toLowerCase()) ||
        t(tool.description).toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        tempDiv.textContent = t(tool.description);
        const height = tempDiv.scrollHeight;
        if (height > maxHeight) {
          maxHeight = height;
        }
      }
    });

    document.body.removeChild(tempDiv);
    return maxHeight;
  }, [activeTab, filteredTools, searchTerm, t]);

  const hasMatchingTool = useMemo(
    () =>
      toolsList.some((category) =>
        category.tools.some(
          (tool) =>
            t(tool.title).toLowerCase().includes(searchTerm.toLowerCase()) ||
            t(tool.description)
              .toLowerCase()
              .includes(searchTerm.toLowerCase()),
        ),
      ),
    [searchTerm, t],
  );

  const isNewTool = (toolId: string) => NEW_TOOL_IDS.includes(toolId);

  const renderToolCard = (tool: any) => (
    <motion.div
      key={tool.id}
      className="relative"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{
        opacity: 0,
        scale: 0.8,
        transition: { duration: 0.2 },
      }}
      layout
      transition={{
        opacity: { duration: 0.2 },
        layout: {
          duration: 0.3,
          type: "spring",
          stiffness: 50,
        },
      }}
    >
      {isNewTool(tool.id) && (
        <motion.div
          className="absolute -inset-[2px] rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-70 blur-[2px] group-hover:opacity-100 transition-all duration-300"
          animate={{
            background: [
              "linear-gradient(45deg, rgb(99 102 241), rgb(168 85 247), rgb(236 72 153))",
              "linear-gradient(45deg, rgb(236 72 153), rgb(99 102 241), rgb(168 85 247))",
              "linear-gradient(45deg, rgb(168 85 247), rgb(236 72 153), rgb(99 102 241))",
            ],
            scale: [1, 1.02, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            background: {
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            },
            scale: {
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            },
            opacity: {
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            },
          }}
        />
      )}

      {/* Main clickable card area */}
      <div
        onClick={() => handleClickDescription(tool.path)}
        className={`group relative rounded-2xl shadow-md hover:shadow-xl p-4 transition-all duration-300 hover:-translate-y-1 bg-white dark:bg-gray-800 border border-transparent cursor-pointer z-10 ${
          getToolColorFromCategory(tool.id as ToolId)?.hover
        }`}
        style={{
          minHeight: `${Math.max(220, 140 + maxDescriptionHeight)}px`,
        }}
      >
        <div className="flex h-full flex-col text-center gap-1">
          <div className="flex flex-row items-center justify-start">
            <div
              className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0`}
            >
              <tool.icon
                className={`w-8 h-8 ${
                  getToolColorFromCategory(tool.id as ToolId)?.iconColor
                }`}
              />
            </div>
            <Text variant="h2" className="text-left text-pretty flex-1">
              {t(tool.title)}
            </Text>
            <div className="w-8 h-8 rounded-full">
              {/* favorite button placeholder */}
            </div>
          </div>

          <div
            className="my-3 flex-1 flex items-start"
            style={{ minHeight: `${maxDescriptionHeight}px` }}
          >
            <Text variant="p" status="secondary" className="text-left w-full">
              {t(tool.description)}
            </Text>
          </div>

          {/* Create button - now using button instead of Link */}
          <div className="mt-auto flex-shrink-0 flex flex-row gap-2 w-full justify-end absolute right-4 bottom-4">
            <button
              onClick={(e) => handleClickGeneration(tool.createPath, e)}
              className={`group flex items-center justify-center rounded-full p-2 transition-all duration-300 bg-gradient-to-r ${
                getToolColorFromCategory(tool.id as ToolId)?.light
              } ${
                getToolColorFromCategory(tool.id as ToolId)?.dark
              } text-white hover:shadow-lg overflow-hidden hover:px-4`}
            >
              <Plus className="w-5 h-5 mx-0.5" />
              <span className="group-hover:mr-2 max-w-0 group-hover:max-w-[100px] whitespace-nowrap overflow-hidden transition-all duration-300 opacity-0 group-hover:opacity-100 ml-0 group-hover:ml-2">
                {t("common.create")}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Favorite button - positioned absolutely to avoid nesting */}
      <div
        className="absolute top-4 right-4 z-20"
        onClick={(e) => e.stopPropagation()}
      >
        <FavoriteButton
          isFavorite={Boolean(favoriteTools?.includes(tool.id))}
          onToggleFavorite={() => toggleFavorite(tool.id as ToolId)}
        />
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 pb-8 pt-4">
        <div className="flex flex-col gap-4 mb-6">
          {/* Tabs section */}
          <div className="relative w-full">
            <div className="relative flex items-center">
              <button
                ref={leftButtonRef}
                onClick={scrollLeft}
                className="absolute left-0 z-10 flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-md border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none opacity-0 transition-opacity duration-200"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>

              <div
                ref={tabsContainerRef}
                className="w-full overflow-x-auto scrollbar-hide py-2 scroll-smooth max-w-[calc(100vw_-_45px)]"
                onScroll={handleScroll}
              >
                <div className="inline-flex items-center">
                  <Button
                    className={`px-4 py-2 mr-2 rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                      activeTab === "favorites"
                        ? getTabColors("favorites").active
                        : getTabColors("favorites").inactive
                    }`}
                    onClick={() => handleTabChange("favorites")}
                  >
                    <div className="flex items-center">
                      <Star className="w-4 h-4 mr-2" />
                      {t("common.favorites")}
                    </div>
                  </Button>

                  <Button
                    className={`px-4 py-2 mr-2 rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                      activeTab === "all"
                        ? getTabColors("all").active
                        : getTabColors("all").inactive
                    }`}
                    onClick={() => handleTabChange("all")}
                  >
                    {t("common.all")}
                  </Button>

                  {toolsList.map((category) => (
                    <button
                      key={category.category}
                      onClick={() => handleTabChange(category.category)}
                      className={`px-4 py-2 mr-2 rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                        activeTab === category.category
                          ? getTabColors(category.category).active
                          : getTabColors(category.category).inactive
                      }`}
                    >
                      {t(category.categoryTitle)}
                    </button>
                  ))}
                </div>
              </div>

              <button
                ref={rightButtonRef}
                onClick={scrollRight}
                className="absolute right-0 z-10 flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-md border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none opacity-0 transition-opacity duration-200"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
          </div>

          <div className="relative w-full md:w-64 lg:self-end xl:self-start xl:absolute xl:right-4 xl:top-5">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full md:max-w-xs pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={t("common.search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="w-full grid grid-cols-1 gap-8">
          {activeTab === "all" ? (
            // Show categories in the "all" tab
            toolsList.map((category) => {
              const filteredCategoryTools = category.tools
                .filter((tool) => tool.id !== "essay-grader")
                .filter((tool) =>
                  searchTerm
                    ? t(tool.title)
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                      t(tool.description)
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                    : true,
                );

              // Only render category if it has matching tools
              if (filteredCategoryTools.length === 0) {
                return null;
              }

              return (
                <div key={category.category} className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-lg flex items-center justify-center ${category.colors.iconBg}`}
                    >
                      <category.icon
                        className={`w-8 h-8 ${category.colors.iconColor}`}
                      />
                    </div>
                    <Text variant="h2">{t(category.categoryTitle)}</Text>
                  </div>

                  <motion.div
                    className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
                    layout
                  >
                    <AnimatePresence mode="popLayout">
                      {filteredCategoryTools.map((tool) =>
                        renderToolCard(tool),
                      )}
                    </AnimatePresence>
                  </motion.div>
                </div>
              );
            })
          ) : (
            // Show filtered tools for other tabs
            <motion.div
              className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
              layout
            >
              <AnimatePresence mode="popLayout">
                {filteredTools
                  .filter((tool) => tool.id !== "essay-grader")
                  .filter((tool) =>
                    searchTerm
                      ? t(tool.title)
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ||
                        t(tool.description)
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                      : true,
                  )
                  .map((tool) => renderToolCard(tool))}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Empty State for No Results */}
          {((activeTab === "all" && !hasMatchingTool) ||
            (activeTab !== "all" && filteredTools.length === 0)) && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-center">
                <Text variant="h2" className="mb-2">
                  {activeTab === "favorites"
                    ? t("common.noFavorites")
                    : t("common.noSearchResults")}
                </Text>
                <Text variant="p" status="secondary">
                  {activeTab === "favorites"
                    ? t("common.addFavoritesHint")
                    : t("common.tryDifferentSearch")}
                </Text>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tools;
